import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { apiBaseURL, buildLabel } from './api/client';
import { Dashboard, type AppTab } from './components/Dashboard';
import type { FeaturedWindow } from './components/FeaturedMarket';
import { moverKey } from './components/LiveMoversTable';
import { useDashboardControls } from './hooks/useDashboardControls';
import { useDashboardData } from './hooks/useDashboardData';
import { useDisplayedMovers } from './hooks/useDisplayedMovers';
import { useLivePriceSamples } from './hooks/useLivePriceSamples';
import { useLiveMovers } from './hooks/useLiveMovers';
import type { DashboardFilters } from './lib/filters';

// Default to Watchlist so the page is never blank when confirmed-band
// movers are quiet. The Watchlist mode surfaces heat 45-64 candidates
// (the "almost confirmed" cohort), which is the product's lead-time
// thesis. The user can flip to Confirmed via the filter row.
const initialFilters: DashboardFilters = {
  window: '5m',
  mode: 'watchlist',
  category: 'all',
  query: '',
};

export function App() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<AppTab>('dashboard');
  const [helpOpen, setHelpOpen] = useState(false);
  const [featuredWindow, setFeaturedWindow] = useState<FeaturedWindow>('1m');
  const [selectedMoverKey, setSelectedMoverKey] = useState<string | null>(null);
  const [filters, setFilters] = useState<DashboardFilters>(initialFilters);
  const { controls, setControls, resetControls } = useDashboardControls();
  const effectiveFilters = useMemo(() => ({ ...filters, query: filters.query.trim() }), [filters]);
  const data = useDashboardData(effectiveFilters);
  const live = useLiveMovers(effectiveFilters);
  const dashboardSnapshot = useDisplayedMovers(data.movers, {
    autoRefresh: controls.autoRefresh,
    intervalSeconds: controls.refreshIntervalSeconds,
    resetKey: JSON.stringify(effectiveFilters),
  });
  const watchlistSnapshot = useDisplayedMovers(data.watchlistFallback, {
    autoRefresh: controls.autoRefresh,
    intervalSeconds: controls.refreshIntervalSeconds,
    resetKey: `watchlist:${effectiveFilters.window}`,
  });
  const samplesByAsset = useLivePriceSamples(data.movers);
  const featuredMover = selectedMoverKey
    ? dashboardSnapshot.displayed.find((mover) => moverKey(mover) === selectedMoverKey) ??
      watchlistSnapshot.displayed.find((mover) => moverKey(mover) === selectedMoverKey)
    : undefined;
  // "Top mover" preference order:
  //   1. Currently selected mover (if any)
  //   2. Top of the user's current-mode displayed list
  //   3. Top of the watchlist fallback (so Featured Market is never empty
  //      when there's any signal in either band)
  const topMover =
    dashboardSnapshot.displayed[0] ??
    watchlistSnapshot.displayed[0];

  useEffect(() => {
    if (selectedMoverKey && hasMoverKey(dashboardSnapshot.displayed, selectedMoverKey)) {
      return;
    }
    const next = dashboardSnapshot.displayed[0] ?? watchlistSnapshot.displayed[0];
    setSelectedMoverKey(next ? moverKey(next) : null);
  }, [dashboardSnapshot.displayed, watchlistSnapshot.displayed, selectedMoverKey]);

  function applyLatestData() {
    dashboardSnapshot.applyNow();
    watchlistSnapshot.applyNow();
    void queryClient.invalidateQueries();
  }

  return (
    <Dashboard
      activeTab={activeTab}
      apiBaseUrl={apiBaseURL()}
      buildLabel={buildLabel()}
      controls={controls}
      featuredWindow={featuredWindow}
      filters={filters}
      featuredMover={featuredMover ?? topMover}
      helpOpen={helpOpen}
      onFiltersChange={setFilters}
      onCloseHelp={() => {
        setHelpOpen(false);
      }}
      onControlsChange={setControls}
      onFeaturedWindowChange={(window) => {
        setFeaturedWindow(window);
      }}
      onOpenHelp={() => {
        setHelpOpen(true);
      }}
      onRefreshData={applyLatestData}
      onResetControls={resetControls}
      onSelectMover={(mover) => {
        setSelectedMoverKey(moverKey(mover));
      }}
      onTabChange={setActiveTab}
      onUseTopMover={() => {
        if (topMover) setSelectedMoverKey(moverKey(topMover));
      }}
      movers={dashboardSnapshot.displayed}
      watchlistFallback={watchlistSnapshot.displayed}
      samplesByAsset={samplesByAsset}
      selectedMoverKey={selectedMoverKey ?? undefined}
      snapshotLastAppliedAt={dashboardSnapshot.lastAppliedAt}
      stats={data.stats}
      summary={data.summary}
      isLoading={data.isLoading}
      isLive={live.isLive}
      lastMessageAt={live.lastMessageAt ?? data.lastUpdatedAt}
      error={data.error ?? live.error}
    />
  );
}

function hasMoverKey(items: readonly { market_id?: string; asset_id: string }[], key: string): boolean {
  return items.some((item) => moverKey(item as never) === key);
}